/**
 * Applications - Wrapper para mantener compatibilidad con código existente
 * 
 * NOTA: Este archivo mantiene la API antigua para facilitar la migración gradual.
 * Los nuevos componentes deberían usar useApp() directamente del Context.
 * 
 * @deprecated Usar useApp() del Context en lugar de Applications
 */
import React from 'react';
import { useApp } from '../context/AppContext';
import logger from '../utils/logger';

// Esta es una instancia singleton que se actualizará con el contexto
// Se mantiene para compatibilidad con código existente
class ApplicationWrapper {
    constructor() {
        // Esta clase será reemplazada por el contexto
        // Se mantiene solo para compatibilidad
        this._context = null;
    }

    setContext(context) {
        this._context = context;
    }

    get state() {
        if (!this._context) {
            logger.warn('Applications usado fuera de AppProvider. Usa useApp() en componentes React.');
            return {};
        }
        return this._context.state;
    }

    get profile() {
        if (!this._context) return null;
        return this._context.profile;
    }

    get kId() {
        return "itfgscomm";
    }

    get idwlKey() {
        return "lwfgscomm";
    }

    updateState(updater) {
        if (!this._context) {
            console.warn('Applications.updateState usado fuera de AppProvider. Usa useApp() en componentes React.');
            return;
        }
        return this._context.updateState(updater);
    }

    getState() {
        if (!this._context) {
            console.warn('Applications.getState usado fuera de AppProvider. Usa useApp() en componentes React.');
            return {};
        }
        return this._context.getState();
    }

    on(propertyName, callback) {
        // Para compatibilidad con código que usa Applications.on()
        // En componentes React, usar useAppState(propertyName) en su lugar
        if (!this._context) {
            console.warn('Applications.on usado fuera de AppProvider. Usa useAppState() en componentes React.');
            return () => {};
        }

        // Implementación básica de suscripción
        // Nota: Esta es una implementación simplificada. Para mejor rendimiento,
        // los componentes deberían usar useAppState() directamente
        let currentValue = this._context.state[propertyName];
        const checkInterval = setInterval(() => {
            const newValue = this._context.state[propertyName];
            if (newValue !== currentValue) {
                currentValue = newValue;
                callback(newValue);
            }
        }, 100);

        return () => clearInterval(checkInterval);
    }

    notify(payload = {}) {
        if (!this._context) {
            console.warn('Applications.notify usado fuera de AppProvider. Usa useApp() en componentes React.');
            return null;
        }
        return this._context.notify(payload);
    }

    removeNotification(payload) {
        if (!this._context) {
            console.warn('Applications.removeNotification usado fuera de AppProvider. Usa useApp() en componentes React.');
            return;
        }
        return this._context.removeNotification(payload);
    }

    parseDataBySelect(object, label) {
        if (!this._context) {
            // Fallback si no hay contexto
            return {
                ...object,
                label: label,
                value: object._id
            };
        }
        return this._context.parseDataBySelect(object, label);
    }

    async loadMain() {
        if (!this._context) {
            console.warn('Applications.loadMain usado fuera de AppProvider. Usa useApp() en componentes React.');
            return;
        }
        return this._context.loadMain();
    }

    async getPermissions() {
        if (!this._context) {
            console.warn('Applications.getPermissions usado fuera de AppProvider. Usa useApp() en componentes React.');
            return { permissions: [], role: "" };
        }
        return this._context.getPermissions();
    }

    getLS(key) {
        if (!this._context) {
            return localStorage.getItem(key);
        }
        return this._context.getLS(key);
    }

    setLS(key, value) {
        if (!this._context) {
            return localStorage.setItem(key, value);
        }
        return this._context.setLS(key, value);
    }
}

// Instancia singleton
const Applications = new ApplicationWrapper();

// Componente para conectar Applications con el Context
export const ApplicationsConnector = () => {
    const context = useApp();
    
    React.useEffect(() => {
        Applications.setContext(context);
    }, [context]);
    
    return null;
};

export default Applications;